import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Building2, Users, ShoppingCart, LogOut, Plus, Search, 
  Settings, BarChart3, Power, PowerOff, ChevronRight,
  Store, DollarSign, TrendingUp, Eye, Edit, Loader2,
  Shield, CheckCircle, XCircle, Trash2, AlertTriangle, Crown, Infinity
} from 'lucide-react';
import FOSWatermark from '../components/FOSWatermark';
import { performLogout } from '../utils/authUtils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('overview');
  const [stats, setStats] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // New client form
  const [showNewClientForm, setShowNewClientForm] = useState(false);
  const [newClient, setNewClient] = useState({
    business_name: '',
    email: '',
    password: '',
    phone: ''
  });
  const [creating, setCreating] = useState(false);
  
  // Selected client for editing
  const [selectedClient, setSelectedClient] = useState(null);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${user.token}` }
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, clientsRes] = await Promise.all([
        axios.get(`${API}/super-admin/stats`, getAuthHeaders()),
        axios.get(`${API}/super-admin/clients`, getAuthHeaders())
      ]);
      
      setStats(statsRes.data);
      setClients(clientsRes.data.clients || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        toast.error('Session expired. Please login again.');
        handleLogout();
      } else {
        toast.error('Failed to load data');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [user.token]);

  const handleLogout = () => {
    // Use performLogout which clears all user data and does a full page reload
    // This prevents data leakage between different user accounts
    performLogout(setUser, null, '/');
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    setCreating(true);
    
    try {
      const response = await axios.post(`${API}/super-admin/clients`, newClient, getAuthHeaders());
      
      if (response.data.success) {
        toast.success(`Client "${newClient.business_name}" created successfully!`);
        setNewClient({ business_name: '', email: '', password: '', phone: '' });
        setShowNewClientForm(false);
        fetchData();
      }
    } catch (error) {
      console.error('Error creating client:', error);
      toast.error(error.response?.data?.detail || 'Failed to create client');
    } finally {
      setCreating(false);
    }
  };

  const handleToggleClientStatus = async (businessId, currentStatus) => {
    try {
      await axios.post(
        `${API}/super-admin/clients/${businessId}/toggle-status`,
        { is_active: !currentStatus },
        getAuthHeaders()
      );
      
      toast.success(`Client ${!currentStatus ? 'enabled' : 'disabled'} successfully`);
      fetchData();
    } catch (error) {
      console.error('Error toggling client status:', error);
      toast.error('Failed to update client status');
    }
  };

  const handleGrantFosForever = async (clientId, grant = true) => {
    try {
      const response = await axios.post(
        `${API}/super-admin/clients/${clientId}/fos-forever`,
        { grant },
        getAuthHeaders()
      );
      
      if (response.data.success) {
        toast.success(response.data.message, { duration: 5000 });
        fetchData();
      }
    } catch (error) {
      console.error('Error managing FOS Forever:', error);
      toast.error(error.response?.data?.detail || 'Failed to update FOS Forever status');
    }
  };

  const handleDeleteClient = async () => {
    if (!clientToDelete) return;
    
    const clientName = clientToDelete.business_name || clientToDelete.company_name || 'Unknown';
    
    // Verify confirmation text
    if (deleteConfirmText !== clientName) {
      toast.error('Please type the exact business name to confirm deletion');
      return;
    }
    
    setDeleting(true);
    try {
      const clientIdToDelete = clientToDelete.client_id;
      const response = await axios.delete(
        `${API}/super-admin/clients/${clientIdToDelete}`,
        getAuthHeaders()
      );
      
      if (response.data.success) {
        toast.success(`Business "${clientName}" deleted successfully`);
        
        // Show deletion summary
        const counts = response.data.deleted_counts;
        console.log('Deleted counts:', counts);
        
        setDeleteDialogOpen(false);
        setClientToDelete(null);
        setDeleteConfirmText('');
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete client');
    } finally {
      setDeleting(false);
    }
  };

  const openDeleteDialog = (client) => {
    setClientToDelete(client);
    setDeleteConfirmText('');
    setDeleteDialogOpen(true);
  };

  const handleUpdateFeatures = async (clientId, features) => {
    try {
      await axios.put(
        `${API}/super-admin/clients/${clientId}/features`,
        { features },
        getAuthHeaders()
      );
      
      toast.success('Features updated successfully');
      fetchData();
    } catch (error) {
      console.error('Error updating features:', error);
      toast.error('Failed to update features');
    }
  };

  const filteredClients = clients.filter(client =>
    client.business_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.client_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-secondary">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-900/80 backdrop-blur-sm border-b border-slate-700 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/fos-logo.png" alt="FOS Logo" className="h-10 sm:h-12 w-auto object-contain" />
            <div className="hidden sm:block">
              <h1 className="text-lg font-bold text-white">Super Admin</h1>
              <p className="text-xs text-slate-400">Platform Management</p>
            </div>
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400 hidden sm:block">
              {user?.full_name || user?.email}
            </span>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Clients</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.total_clients || 0}</p>
                </div>
                <div className="p-3 bg-blue-500/20 rounded-xl">
                  <Building2 className="h-6 w-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Active Clients</p>
                  <p className="text-3xl font-bold text-green-400 mt-1">{stats?.active_clients || 0}</p>
                </div>
                <div className="p-3 bg-green-500/20 rounded-xl">
                  <CheckCircle className="h-6 w-6 text-green-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Franchises</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.total_franchises || 0}</p>
                </div>
                <div className="p-3 bg-purple-500/20 rounded-xl">
                  <Store className="h-6 w-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400">Total Orders</p>
                  <p className="text-3xl font-bold text-white mt-1">{stats?.total_orders?.toLocaleString() || 0}</p>
                </div>
                <div className="p-3 bg-amber-500/20 rounded-xl">
                  <ShoppingCart className="h-6 w-6 text-amber-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Clients Section */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader className="border-b border-slate-700">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-white flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-amber-400" />
                  Client Management
                </CardTitle>
                <CardDescription className="text-slate-400 mt-1">
                  Manage all registered businesses
                </CardDescription>
              </div>
              <Button
                onClick={() => setShowNewClientForm(true)}
                className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Client
              </Button>
            </div>
          </CardHeader>
          
          <CardContent className="p-6">
            {/* Search */}
            <div className="relative mb-6">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <Input
                placeholder="Search by name, email, or business ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500"
              />
            </div>

            {/* Clients List */}
            <div className="space-y-4">
              {filteredClients.length === 0 ? (
                <div className="text-center py-12">
                  <Building2 className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-400">No clients found</p>
                </div>
              ) : (
                filteredClients.map((client) => (
                  <div
                    key={client.client_id}
                    className={`bg-slate-900/50 rounded-xl p-4 border transition-colors ${
                      client.subscription?.is_forever 
                        ? 'border-yellow-500/50 bg-gradient-to-r from-yellow-900/20 to-slate-900/50' 
                        : 'border-slate-700 hover:border-slate-600'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 flex-wrap">
                          <h3 className="text-lg font-semibold text-white">
                            {client.business_name}
                          </h3>
                          {client.subscription?.is_forever && (
                            <span className="px-2 py-0.5 bg-gradient-to-r from-yellow-500 to-amber-500 text-black text-xs rounded-full font-bold flex items-center gap-1">
                              <Crown className="h-3 w-3" />
                              FOS Forever
                            </span>
                          )}
                          {client.is_active ? (
                            <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs rounded-full">
                              Active
                            </span>
                          ) : (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
                              Disabled
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 mt-1">{client.email}</p>
                        <div className="flex items-center gap-3 mt-1">
                          <p className="text-xs text-slate-500 font-mono">
                            ID: {client.client_id}
                          </p>
                          {client.subscription?.plan_name && (
                            <p className="text-xs text-slate-400">
                              Plan: <span className={client.subscription?.is_forever ? 'text-yellow-400 font-semibold' : ''}>
                                {client.subscription.plan_name}
                              </span>
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-wrap justify-end">
                        {/* FOS Forever Button */}
                        {client.subscription?.is_forever ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGrantFosForever(client.client_id, false)}
                            className="border-yellow-600 text-yellow-400 hover:bg-yellow-900/30"
                            title="Revoke FOS Forever"
                          >
                            <Crown className="h-4 w-4 mr-1" />
                            Revoke VIP
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleGrantFosForever(client.client_id, true)}
                            className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                            title="Grant FOS Forever (Lifetime VIP)"
                          >
                            <Crown className="h-4 w-4 mr-1" />
                            Grant VIP
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedClient(client)}
                          className="border-slate-600 text-slate-300 hover:bg-slate-700"
                        >
                          <Settings className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleToggleClientStatus(client.client_id || client.client_id, client.is_active)}
                          className={client.is_active 
                            ? "border-red-600 text-red-400 hover:bg-red-900/30"
                            : "border-green-600 text-green-400 hover:bg-green-900/30"
                          }
                        >
                          {client.is_active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteDialog(client)}
                          className="border-red-700 text-red-500 hover:bg-red-900/50"
                          title="Delete Business"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Features Quick View */}
                    <div className="mt-4 flex flex-wrap gap-2">
                      {Object.entries(client.features_enabled || {}).map(([feature, enabled]) => (
                        <span
                          key={feature}
                          className={`px-2 py-1 rounded text-xs ${
                            enabled 
                              ? 'bg-slate-700 text-slate-300' 
                              : 'bg-slate-800 text-slate-500 line-through'
                          }`}
                        >
                          {feature.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <AlertTriangle className="h-5 w-5" />
              Delete Business Permanently
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              This action cannot be undone. This will permanently delete the business and all associated data.
            </DialogDescription>
          </DialogHeader>
          
          {clientToDelete && (
            <div className="space-y-4 py-4">
              <div className="bg-red-900/20 border border-red-800 rounded-lg p-4">
                <p className="text-sm text-red-400 mb-2">You are about to delete:</p>
                <p className="font-bold text-white">{clientToDelete.business_name || clientToDelete.company_name}</p>
                <p className="text-xs text-slate-400 mt-1">ID: {clientToDelete.client_id}</p>
                <p className="text-xs text-slate-400">Email: {clientToDelete.email}</p>
              </div>
              
              <div className="bg-slate-800/50 rounded-lg p-3 text-xs text-slate-400">
                <p className="font-medium text-slate-300 mb-2">This will delete:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>All franchises</li>
                  <li>All orders and transactions</li>
                  <li>All staff records</li>
                  <li>All menu items</li>
                  <li>All ledger data</li>
                  <li>All raw materials</li>
                </ul>
              </div>
              
              <div>
                <Label className="text-slate-300 text-sm">
                  Type <span className="font-bold text-red-400">&quot;{clientToDelete.business_name || clientToDelete.company_name}&quot;</span> to confirm:
                </Label>
                <Input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder="Type business name here..."
                  className="mt-2 bg-slate-800 border-slate-600 text-white"
                />
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300"
                  onClick={() => {
                    setDeleteDialogOpen(false);
                    setClientToDelete(null);
                    setDeleteConfirmText('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  className="flex-1 bg-red-600 hover:bg-red-700"
                  onClick={handleDeleteClient}
                  disabled={deleting || deleteConfirmText !== (clientToDelete.business_name || clientToDelete.company_name)}
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Permanently
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* New Client Modal */}
      {showNewClientForm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-md bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">Create New Client</CardTitle>
              <CardDescription className="text-slate-400">
                Add a new business to the platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateClient} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Business Name</Label>
                  <Input
                    value={newClient.business_name}
                    onChange={(e) => setNewClient({...newClient, business_name: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white"
                    placeholder="Acme Coffee Co."
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Email</Label>
                  <Input
                    type="email"
                    value={newClient.email}
                    onChange={(e) => setNewClient({...newClient, email: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white"
                    placeholder="admin@example.com"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Password</Label>
                  <Input
                    type="password"
                    value={newClient.password}
                    onChange={(e) => setNewClient({...newClient, password: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white"
                    placeholder="Strong password"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Phone (Optional)</Label>
                  <Input
                    value={newClient.phone}
                    onChange={(e) => setNewClient({...newClient, phone: e.target.value})}
                    className="bg-slate-900 border-slate-600 text-white"
                    placeholder="+91 9876543210"
                  />
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowNewClientForm(false)}
                    className="flex-1 border-slate-600 text-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={creating}
                    className="flex-1 bg-gradient-to-r from-amber-500 to-orange-600"
                  >
                    {creating ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Plus className="h-4 w-4 mr-2" />
                    )}
                    Create Client
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Client Settings Modal */}
      {selectedClient && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg bg-slate-800 border-slate-700">
            <CardHeader>
              <CardTitle className="text-white">{selectedClient.business_name}</CardTitle>
              <CardDescription className="text-slate-400">
                Manage features and settings
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400">Business ID</p>
                  <p className="text-white font-mono">{selectedClient.client_id}</p>
                </div>
                
                <div className="bg-slate-900/50 rounded-lg p-4">
                  <p className="text-sm text-slate-400 mb-3">Enabled Features</p>
                  <div className="space-y-3">
                    {Object.entries(selectedClient.features_enabled || {}).map(([feature, enabled]) => (
                      <div key={feature} className="flex items-center justify-between">
                        <span className="text-white capitalize">{feature.replace(/_/g, ' ')}</span>
                        <Switch
                          checked={enabled}
                          onCheckedChange={(checked) => {
                            const updatedFeatures = {
                              ...selectedClient.features_enabled,
                              [feature]: checked
                            };
                            handleUpdateFeatures(selectedClient.client_id, updatedFeatures);
                            setSelectedClient({
                              ...selectedClient,
                              features_enabled: updatedFeatures
                            });
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                
                <Button
                  onClick={() => setSelectedClient(null)}
                  className="w-full bg-slate-700 hover:bg-slate-600"
                >
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* FOS Watermark */}
      <FOSWatermark opacity={0.06} size="lg" />
    </div>
  );
};

export default SuperAdminDashboard;
